/**
 * RGR04 반품관리 요청·응답 타입. moms-be 실제 코드 기준(계획서 3장 근거).
 *
 * 원장은 `IIF_MOMSAN_TYPE1`(`MENU_CD LIKE 'RGR04%'`)이고, 응답 필드는 전부 카멜케이스다.
 * `plnDate` 는 `yyyyMMdd` 문자열, `insDate`·`updDate` 는 `yyyyMMddHHmmss` 문자열이다.
 */

/** 조회 결과 한 행 — `ReturnManagementDto`, 41필드. */
export interface ReturnManagementItemRes {
  id: number
  shipperName: string | null
  sprNm: string | null
  ordNo: string | null
  ordOptNo: string | null
  /** `yyyyMMdd` */
  plnDate: string | null
  rsnCode: string | null
  rtnType: string | null
  rlChannel: string | null
  barcode: string | null
  skuId: string | null
  brdNm: string | null
  gdsCd: string | null
  gdsNm: string | null
  gdsOpt: string | null
  ordQty: number | null
  trsNm: string | null
  trsPhone: string | null
  trsAddr: string | null
  trsNumber: string | null
  trsInvoiceNo: string | null
  trsLspNm: string | null
  salChannel: string | null
  procNm: string | null
  refNo1: string | null
  refNo2: string | null
  refNo3: string | null
  etc1: string | null
  etc2: string | null
  etc3: string | null
  etc4: string | null
  etc5: string | null
  flag: boolean | null
  apiFlag: boolean | null
  jobCommentForInvoice: string | null
  claimId: string | null
  /** `yyyyMMddHHmmss` */
  insDate: string | null
  insBy: string | null
  /** `yyyyMMddHHmmss` */
  updDate: string | null
  updBy: string | null
  /** `"[509676]"` 형태의 원본 문자열. 경로 확장은 하지 않는다(3.1). */
  fileId: string | null
}

/** 조회 요청 쿼리 — 전부 optional. */
export interface GetReturnManagementReq {
  /** `SPR_NM` 완전일치(화주 코드) */
  shipper?: string
  /** `yyyyMMdd` */
  plnDateFrom?: string
  /** `yyyyMMdd` */
  plnDateTo?: string
  /** 생성자 부분일치 */
  insBy?: string
  /** 반품처 완전일치 */
  rlChannel?: string
}

/** 저장 요청 한 행 — create·update 공용. 부분 수정이 아니라 행 전체를 다시 보낸다(3.2). */
export interface ReturnManagementWriteBody {
  /** update 만 필수, 양수 */
  id?: number
  ordNo?: string
  ordOptNo?: string
  /** 필수, `yyyyMMdd` */
  plnDate: string
  rsnCode?: string
  rtnType?: string
  rlChannel?: string
  barcode?: string
  brdNm?: string
  gdsCd?: string
  gdsNm?: string
  gdsOpt?: string
  /** 필수 */
  ordQty: number
  /** 필수, 공백 불가 */
  trsNm: string
  trsPhone?: string
  /** 필수, 공백 불가 */
  trsAddr: string
  trsNumber?: string
  /** 필수, 공백 불가 */
  trsInvoiceNo: string
  trsLspNm?: string
  salChannel?: string
  procNm?: string
  refNo1?: string
  refNo2?: string
  refNo3?: string
  etc1?: string
  etc2?: string
  etc3?: string
  etc4?: string
  etc5?: string
  jobCommentForInvoice?: string
  claimId?: string
  /**
   * 검색(FLAG) — 행 조회 방식 스위치. `IIF_MOMSAN_TYPE1.FLAG` 로 저장된다(moms-be #3666).
   * 보내지 않으면 create 는 false 로 저장되고, update 는 기존 값을 그대로 둔다.
   */
  flag?: boolean
  /** 반드시 null. 값을 넣으면 400(3.2) — 첨부는 저장 이후 별도 API 로 붙인다. */
  fileId: null
}

export interface SaveReturnManagementReq {
  /** 필수, 공백 불가 */
  shipper: string
  creates: ReturnManagementWriteBody[]
  updates: (ReturnManagementWriteBody & { id: number })[]
  deleteIds: number[]
}

export interface SaveReturnManagementRes {
  createdIds: number[]
  updatedIds: number[]
  deletedIds: number[]
}

export interface ConfirmReturnManagementReq {
  /** 공백 불가 */
  shipperCode: string
}

export interface ConfirmReturnManagementRes {
  affectedRows: number
}

/** 엑셀 다운로드 요청 — 조회 조건 5개 + 사유·비밀번호(3.4). */
export interface DownloadReturnManagementReq extends GetReturnManagementReq {
  /** 공백 불가 */
  reasonCode: string
  /** 5자 이상 */
  detailedReason: string
  /** 8자 이상, 대문자·소문자·숫자·특수문자 각 1개 이상, 공백 불가 (D9) */
  password: string
}

/** 주문검색 팝업(송장 REF_NO3 헬퍼) 요청 — `GET /api/v1/return/management/order-search`(3.6). */
export interface OrderSearchReq {
  /** 필수. 출고일자 하한 */
  actDateFrom: string
  /** 필수. 출고일자 상한 */
  actDateTo: string
  shipper?: string
  /** 다건 */
  barcode?: string[]
  /** 다건 */
  refNo1?: string[]
  trsInvoiceNo?: string
  ordNm?: string
  trsNm?: string
  trsPhone?: string
  trsAddr?: string
}

export interface OrderSearchItemRes {
  sprNm: string | null
  actDate: string | null
  ordNm: string | null
  trsNm: string | null
  trsMobile: string | null
  trsPhone: string | null
  trsZip: string | null
  trsAddr: string | null
  barcode: string | null
  actQty: number | null
  refNo1: string | null
  salChannel: string | null
  brdNm: string | null
  gdsCd: string | null
  gdsNm: string | null
  gdsOpt: string | null
  trsInvoiceNo: string | null
  refNo3: string | null
  ordQty: number | null
}

/** 검색(FLAG) 셀 값 — 응답의 `boolean | null` 에 대응하는 화면 표기. */
export type ReturnManagementFlagText = 'Y' | 'N' | ''

/**
 * 응답 `flag` → 셀 값. null 은 빈 칸으로 둔다 — 없는 값을 'N' 으로 채워 두면 다른 칸만 고친
 * 저장에서 사용자가 고르지 않은 false 가 서버로 나간다(BE 는 미전송을 "기존 유지"로 다룬다).
 */
export const toFlagText = (flag?: boolean | null): ReturnManagementFlagText => (flag == null ? '' : flag ? 'Y' : 'N')

/** 검색(FLAG) 셀에서 고를 수 있는 값. 빈 칸은 응답이 null 일 때의 표시일 뿐 선택지가 아니다. */
export type ReturnManagementFlagChoice = Exclude<ReturnManagementFlagText, ''>

/**
 * 셀 값 → 저장 계약 `flag`. 빈 칸(그리고 양식 밖 값)은 undefined 로 빼 서버 기본 동작에 맡긴다.
 *
 * 파라미터를 `ReturnManagementFlagText` 로 좁히지 않는다(리뷰 반영). 행의 `flag` 는 타입 검사를
 * 거치지 않는 두 경로로 써진다 — 표 셀의 `onRowsChange` 와 엑셀 업로드의 캐스팅
 * (useReturnManagementRows.ts `item as Partial<ReturnManagementRow>`). 좁히면 호출부가 주지 않는
 * 보장을 시그니처가 주장하게 되고, 마지막 런타임 방어만 없어진다.
 */
export const toFlagBoolean = (text: string): boolean | undefined => (text === 'Y' ? true : text === 'N' ? false : undefined)

/**
 * 화면 편집 행 — 조회 결과에 표 로컬 상태(행 키·첨부/동영상 개수)를 얹은 것.
 *
 * `plnDate` 는 여기서부터 화면 표기(`YYYY-MM-DD`)로 바뀐다 — pbo-core `datepicker` 셀이
 * `YYYY-MM-DD` 로 값을 내놓기 때문이다(실측: dist onChange `z.format("YYYY-MM-DD")`).
 * BE 계약(`yyyyMMdd`)으로의 변환은 저장·다운로드 요청을 만들 때 한 번만 한다.
 *
 * `flag`(검색)도 같은 사정으로 화면 표기(`'Y'`·`'N'`)를 든다 — `select` 셀은 옵션 `value` 문자열을
 * 그대로 행에 써 넣는다(실측: dist onChange `n.onChange(rowKey, dataIndex, c)`). 응답의 `boolean`
 * 과 저장 계약의 `boolean` 사이 변환은 `toEditableRows`·`toWriteBody` 두 곳에서만 한다.
 */
export type ReturnManagementRow = Omit<ReturnManagementItemRes, 'plnDate' | 'flag'> & {
  /** 표 rowKey. 조회 행은 `r-${id}`, 신규 행은 `new-${seq}`. */
  rowKey: string
  /** `YYYY-MM-DD`. 신규 행은 빈 문자열로 시작한다. */
  plnDate: string
  /** `'Y'`·`'N'`, 응답이 null 이면 빈 문자열(미선택). */
  flag: ReturnManagementFlagText
  /** 신규 행(아직 저장 전) 여부 — id 가 없어 첨부를 붙일 수 없다(4.5, 피드백 7). */
  isNew: boolean
  /** 첨부 개수 — `ATCH_FILE` 셀 값(3.7 `seedMediaFiles` 는 숫자일 때만 개수를 센다). */
  atchFileCount: number
  /** 동영상 개수 — `VIDEO` 셀 값. */
  videoCount: number
}

const toDashDate = (yyyymmdd?: string | null): string => {
  if (yyyymmdd?.length !== 8) return ''
  return `${yyyymmdd.slice(0, 4)}-${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(6, 8)}`
}

/** 화면 표기(`YYYY-MM-DD`) → BE 계약(`yyyyMMdd`). 형식이 아니면 빈 문자열(서버가 필수 검증으로 걸러낸다). */
export const toCompactDate = (dashDate?: string | null): string => {
  if (!dashDate) return ''
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dashDate)
  return matched ? `${matched[1]}${matched[2]}${matched[3]}` : ''
}

/** 조회 응답 → 화면 행. 첨부·동영상 개수는 별도 호출(3.5) 결과를 나중에 얹으므로 여기서는 0으로 둔다. */
export const toEditableRows = (items: ReturnManagementItemRes[]): ReturnManagementRow[] =>
  items.map((item) => ({
    ...item,
    rowKey: `r-${item.id}`,
    plnDate: toDashDate(item.plnDate),
    flag: toFlagText(item.flag),
    isNew: false,
    atchFileCount: 0,
    videoCount: 0,
  }))

/**
 * 화면 행 → 저장 본문(create·update 공용 완전행).
 *
 * `fileId` 는 항상 null 로 보낸다 — 서버가 값을 받으면 400(3.2). 수량은 `amountInput` 셀이 문자열로
 * 편집 값을 내놓으므로(숫자만 남기고 잘라낸 문자열) 여기서 숫자로 되돌린다.
 */
export const toWriteBody = (row: ReturnManagementRow): ReturnManagementWriteBody => ({
  ordNo: row.ordNo || undefined,
  ordOptNo: row.ordOptNo || undefined,
  plnDate: toCompactDate(row.plnDate),
  rsnCode: row.rsnCode || undefined,
  rtnType: row.rtnType || undefined,
  rlChannel: row.rlChannel || undefined,
  barcode: row.barcode || undefined,
  brdNm: row.brdNm || undefined,
  gdsCd: row.gdsCd || undefined,
  gdsNm: row.gdsNm || undefined,
  gdsOpt: row.gdsOpt || undefined,
  ordQty: Number(row.ordQty) || 0,
  trsNm: row.trsNm || '',
  trsPhone: row.trsPhone || undefined,
  trsAddr: row.trsAddr || '',
  trsNumber: row.trsNumber || undefined,
  trsInvoiceNo: row.trsInvoiceNo || '',
  trsLspNm: row.trsLspNm || undefined,
  salChannel: row.salChannel || undefined,
  procNm: row.procNm || undefined,
  refNo1: row.refNo1 || undefined,
  refNo2: row.refNo2 || undefined,
  refNo3: row.refNo3 || undefined,
  etc1: row.etc1 || undefined,
  etc2: row.etc2 || undefined,
  etc3: row.etc3 || undefined,
  etc4: row.etc4 || undefined,
  etc5: row.etc5 || undefined,
  jobCommentForInvoice: row.jobCommentForInvoice || undefined,
  claimId: row.claimId || undefined,
  flag: toFlagBoolean(row.flag),
  fileId: null,
})
